// Complaint lifecycle helpers for the Journey page's Complaints panel
// (docs/complaint-lifecycle-plan.md §5).
//
// allowedTransitions mirrors complaint_transition() in
// db/migrations/20260913110200_add_complaint_functions.sql for rendering
// only. The database is the authority: a refused move comes back from the API
// as a 409 whose message the panel shows as-is.

export const STAGES = ["logged", "under_review", "in_progress", "resolved", "closed"];

export const STAGE_META = {
  logged: { label: "Logged", badge: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-500" },
  under_review: { label: "Under Review", badge: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  in_progress: { label: "In Progress", badge: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  resolved: { label: "Resolved", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  closed: { label: "Closed", badge: "bg-slate-50 text-slate-500 border-slate-200", dot: "bg-slate-700" },
};

export const MESSAGE_ACTION_LABEL = {
  opened: "Raised",
  follow_up: "Followed up",
  customer_resolved: "Said it's fixed",
  still_unresolved: "Said it's not fixed",
  staff_replied: "Replied",
};

const DAY_MS = 86_400_000;

export function stageIndex(stage) {
  return STAGES.indexOf(stage);
}

// Collapses the five real stages into the three that matter for a quick
// glance -- in_progress reads as Under Review, closed reads as Resolved.
// Used everywhere a complaint's stage is summarised in one word (the card's
// badge, the 3-step tracker), so they never drift out of sync with each other.
export function visualStage(stage) {
  if (stage === "in_progress") return "under_review";
  if (stage === "closed") return "resolved";
  return stage === "under_review" || stage === "resolved" ? stage : "logged";
}

// Categories are per-brand keys ("service_quality"), so the label is derived
// rather than looked up — a brand-added category needs no frontend change.
export function categoryLabel(key) {
  const text = String(key || "other").replace(/_/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// complaint_type on a pending candidate — fixed two-way split, not a
// per-brand category, so a plain lookup is enough.
export function complaintTypeLabel(key) {
  return key === "behavioural" ? "Behavioural" : "Automobile";
}

export function formatStamp(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// When the complaint last entered `stage`. A reopened complaint enters
// in_progress more than once; the tracker shows the latest time.
export function stageEnteredAt(history, stage) {
  let latest = null;
  for (const h of history || []) {
    if (h.to_stage !== stage) continue;
    if (!latest || new Date(h.changed_at) > new Date(latest)) latest = h.changed_at;
  }
  return latest;
}

export function canReopen(complaint, reopenWindowDays = 7, now = new Date()) {
  if (complaint?.stage === "resolved") return true;
  if (complaint?.stage !== "closed" || !complaint.closed_at) return false;
  return now.getTime() - new Date(complaint.closed_at).getTime() <= reopenWindowDays * DAY_MS;
}

// The moves a manager can make from the dashboard, primary first.
export function allowedTransitions(complaint, { reopenWindowDays = 7, now = new Date() } = {}) {
  const closeEarly = { to: "closed", label: "Close without resolving", needsNote: true };
  switch (complaint?.stage) {
    case "logged":
      return [
        { to: "under_review", label: "Mark under review", primary: true },
        { to: "in_progress", label: "Start work" },
        { to: "resolved", label: "Mark resolved" },
        closeEarly,
      ];
    case "under_review":
      return [
        { to: "in_progress", label: "Start work", primary: true },
        { to: "resolved", label: "Mark resolved" },
        closeEarly,
      ];
    case "in_progress":
      return [{ to: "resolved", label: "Mark resolved", primary: true }, closeEarly];
    // Resolved is terminal as far as reopening goes -- no dashboard reopen,
    // no reopen from the customer saying it's still broken. Manually closing
    // it early (instead of waiting for auto-close) is still allowed.
    case "resolved":
      return [{ to: "closed", label: "Close", primary: true }];
    case "closed":
      return [];
    default:
      return [];
  }
}

export function actorLabel(change) {
  switch (change?.actor_type) {
    case "system":
      return change.to_stage === "logged" ? "AI" : "Auto-close";
    case "customer_whatsapp":
      return "Customer";
    case "staff_whatsapp":
      return "Staff reply";
    default:
      return change?.changed_by_email || "Staff";
  }
}

// Linked messages and stage changes, newest first. A stage change caused by a
// message (same message_id) is folded into that message's entry, so the
// timeline reads "Customer: 'working fine now' → Resolved" rather than two
// rows with the same timestamp.
export function mergeTimeline(history = [], messages = []) {
  const entries = [];
  const byMessageId = new Map();
  for (const m of messages || []) {
    const entry = {
      kind: "message",
      key: `m-${m.id}`,
      at: m.created_at,
      action: m.action,
      text: m.message_text || "",
      reason: m.ai_reason || "",
      senderRole: m.sender_role || (m.action === "staff_replied" ? "staff" : "customer"),
      changes: [],
    };
    entries.push(entry);
    if (!byMessageId.has(m.message_id)) byMessageId.set(m.message_id, entry);
  }
  for (const h of history || []) {
    const owner = h.message_id ? byMessageId.get(h.message_id) : null;
    if (owner) owner.changes.push(h);
    else entries.push({ kind: "stage", key: `h-${h.id}`, at: h.changed_at, change: h });
  }
  return entries.sort((a, b) => new Date(b.at) - new Date(a.at));
}

// Whole days until a Resolved complaint auto-closes; null when not Resolved.
export function autoCloseInDays(complaint, autoCloseDays = 3, now = new Date()) {
  if (complaint?.stage !== "resolved" || !complaint.resolved_at) return null;
  const due = new Date(complaint.resolved_at).getTime() + autoCloseDays * DAY_MS;
  return Math.max(0, Math.ceil((due - now.getTime()) / DAY_MS));
}

// Resolved complaints are operationally closed even while they remain in the
// resolved stage during the auto-close window.
export function filterComplaints(complaints, filter = "open") {
  const list = (complaints || []).filter((c) => {
    if (filter === "all") return true;
    const isClosed = c.stage === "resolved" || c.stage === "closed";
    return filter === "closed" ? isClosed : !isClosed;
  });
  return [...list].sort((a, b) => {
    const aClosed = a.stage === "resolved" || a.stage === "closed";
    const bClosed = b.stage === "resolved" || b.stage === "closed";
    if (aClosed !== bClosed) return aClosed ? 1 : -1;
    return (b.complaint_no || 0) - (a.complaint_no || 0);
  });
}
